<template>
  <div class="file-card" :class="classes" @click="onClick">
    <div class="file-card__preview">
      <div v-if="isImage">
        <img class="file-card__preview__img-elmt" :src="fileData">
      </div>
      <div v-else>
        <Icon
          class="file-card__preview__icon-elmt"
          :color="iconInfo.color"
          :size="92"
        >
          {{ iconInfo.icon }}
        </Icon>
      </div>
    </div>
    <div class="file-card__title">
      <div class="file-card__icon">
        <Icon :color="iconInfo.color">
          {{ iconInfo.icon }}
        </Icon>
      </div>
      <div class="file-card__name">
        {{ file.name }}
      </div>
    </div>
  </div>
</template>

<script>
/**
 * File card - Basic information and thumbnail representing a file
 *
 * @vue-prop {Object} file - File information.
 * @vue-prop {Boolean} selected - Applies a selection color to the card.
 * @vue-event {Object} click - Card click event
 * @vue-event {Object} doubleClick - Card double click event
 */

import { FETCH_FILE_DATA } from '@/store/modules/file/action-types';
// Import components
import Icon from '@/components/icon.vue';
// Import utils
import { RE_IMAGE_TYPE, getFileIconInfo, placeholderImg } from '@/utils/file';


const DOUBLE_CLICK_DELAY = 400; // ms

export default {
  name: 'FileCard',

  components: {
    Icon,
  },

  props: {
    file: {
      type: Object,
      required: true,
    },
    selected: Boolean,
  },

  data() {
    return {
      lastClickTimestamp: 0,
    };
  },

  computed: {
    classes() {
      return {
        'file-card--selected': this.selected,
      };
    },
    iconInfo() {
      return getFileIconInfo(this.file.type);
    },
    isImage() {
      return RE_IMAGE_TYPE.test(this.file.type);
    },
    fileData() {
      return this.$store.state.file.fileData[`${this.file.id}`] || placeholderImg;
    },
  },

  created() {
    if (this.isImage) {
      this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: this.file.id });
    }
  },

  methods: {
    onClick(event) {
      const timestamp = Date.now();
      if (timestamp - this.lastClickTimestamp < DOUBLE_CLICK_DELAY) {
        this.$emit('doubleClick', { event, file: this.file });
        this.lastClickTimestamp = 0;
      } else {
        this.$emit('click', { event, file: this.file });
        this.lastClickTimestamp = timestamp;
      }
    },
  },
};
</script>

<style lang="scss">
.file-card {
  border: 1px solid #dadce0;
  border-radius: 6px;
  color: #ddd;
  display: flex;
  flex-direction: column;
  height: 250px;
  margin: 6px;
  user-select: none;
  width: 210px;

  &__preview {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
    overflow: hidden;

    &__img-elmt {
      height: auto;
      width: 100%;
    }
  }
  &__title {
    align-items: center;
    border-top: 1px solid #dadce0;
    display: flex;
    flex-direction: row;
    flex: 0 0 48px;
  }
  &__icon {
    height: 48px;
    padding: 12px 16px;
    width: 56px;
  }
  &__name {
    align-self: center;
    flex: 1 1 auto;
    font-weight: 600;
    overflow: hidden;
    padding-right: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &--selected {
    background-color: rgba(96, 187, 240, 0.418);
    color: #fff;
  }
}
</style>
